import {
  DynamicFormConfig,
  FormField,
  GridPosition,
} from '../../shared/components/forms/dynamic-form/models/dynamic-form.model';
import { abnCustomValidator } from '../../shared/validators/abn.validator';

/**
 * Validation patterns used across all forms
 */
export const VALIDATION_PATTERNS = {
  EMAIL: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
  PHONE: '^[+]?[0-9][0-9\\s\\-]{6,18}[0-9]$',
  ABN: '^[0-9]{11}$',
  URL: '^(https?://)?(www\\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\\.[a-z]{2,6}\\b([-a-zA-Z0-9@:%_+.~#?&//=]*)$',
  ALPHA_ONLY: '^[a-zA-Z\\s]+$',
  ALPHANUMERIC: '^[a-zA-Z0-9\\s]+$',
  NUMERIC: '^[0-9]+$',
} as const;

/**
 * Common grid position presets
 */
export const GRID_POSITIONS = {
  FULL_WIDTH: { colSpan: 12 } as GridPosition,
  HALF_WIDTH: { colSpan: 6 } as GridPosition,
  THIRD_WIDTH: { colSpan: 4 } as GridPosition,
  QUARTER_WIDTH: { colSpan: 3 } as GridPosition,
} as const;

/**
 * Common field length constraints
 */
export const FIELD_LENGTHS = {
  NAME: { min: 2, max: 50 },
  NAME_LONG: { min: 2, max: 200 },
  SHORT_TEXT: { min: 1, max: 50 },
  MEDIUM_TEXT: { min: 1, max: 100 },
  LONG_TEXT: { min: 1, max: 200 },
  DESCRIPTION: { min: 10, max: 500 },
  ADDRESS: { min: 10, max: 500 },
  EMAIL: { max: 100 },
  PHONE: { max: 20 },
  ABN: { length: 11 },
} as const;

/**
 * Base class for form configurations with common field factories
 */
export abstract class BaseFormConfig {
  /**
   * Creates a hidden ID field
   */
  protected static createIdField(
    formControlName: string = 'id',
    fieldId: string = 'id'
  ): FormField {
    return {
      id: fieldId,
      controlType: 'input',
      formControlName,
      config: {
        label: 'ID',
        type: 'hidden',
      },
      visible: false,
    };
  }

  /**
   * Creates a standard text input field
   */
  protected static createTextField(options: {
    id: string;
    formControlName: string;
    label: string;
    placeholder?: string;
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    hint?: string;
    gridPosition?: GridPosition;
  }): FormField {
    const {
      id,
      formControlName,
      label,
      placeholder = `Enter ${label}`,
      required = false,
      minLength,
      maxLength,
      hint,
      gridPosition = GRID_POSITIONS.HALF_WIDTH,
    } = options;

    const errorMessages: Record<string, string> = {};
    if (required) errorMessages['required'] = `${label} is required`;
    if (minLength) {
      errorMessages['minLength'] = `${label} must be at least ${minLength} characters`;
      errorMessages['minlength'] = errorMessages['minLength'];
    }
    if (maxLength) {
      errorMessages['maxLength'] = `${label} cannot exceed ${maxLength} characters`;
      errorMessages['maxlength'] = errorMessages['maxLength'];
    }

    return {
      id,
      controlType: 'input',
      formControlName,
      config: {
        label,
        placeholder,
        type: 'text',
        required,
        maxLength,
        tooltip: hint,
        showErrors: true,
      },
      validation:
        required || minLength || maxLength
          ? {
              required,
              minLength,
              maxLength,
              errorMessages,
            }
          : undefined,
      gridPosition,
    };
  }

  /**
   * Creates an email input field
   */
  protected static createEmailField(
    options: {
      id?: string;
      formControlName?: string;
      label?: string;
      required?: boolean;
      maxLength?: number;
      gridPosition?: GridPosition;
    } = {}
  ): FormField {
    const {
      id = 'email',
      formControlName = 'email',
      label = 'Email',
      required = true,
      maxLength = FIELD_LENGTHS.EMAIL.max,
      gridPosition = GRID_POSITIONS.HALF_WIDTH,
    } = options;

    return {
      id,
      controlType: 'input',
      formControlName,
      config: {
        label,
        placeholder: `Enter ${label}`,
        type: 'email',
        required,
        maxLength,
      },
      validation: {
        required,
        pattern: VALIDATION_PATTERNS.EMAIL,
        maxLength,
        errorMessages: {
          required: `${label} is required`,
          pattern: 'Please enter a valid email address',
          maxLength: `${label} cannot exceed ${maxLength} characters`,
        },
      },
      gridPosition,
    };
  }

  /**
   * Creates a phone number input field
   */
  protected static createPhoneField(
    options: {
      id?: string;
      formControlName?: string;
      label?: string;
      required?: boolean;
      maxLength?: number;
      gridPosition?: GridPosition;
    } = {}
  ): FormField {
    const {
      id = 'phoneNumber',
      formControlName = 'phoneNumber',
      label = 'Phone Number',
      required = false,
      maxLength = FIELD_LENGTHS.PHONE.max,
      gridPosition = GRID_POSITIONS.HALF_WIDTH,
    } = options;

    const validation: any = {
      pattern: VALIDATION_PATTERNS.PHONE,
      maxLength,
      errorMessages: {
        pattern: 'Please enter a valid phone number (e.g., 0412345678 or +61 412 345 678)',
        maxLength: `${label} cannot exceed ${maxLength} characters`,
      },
    };

    if (required) {
      validation.required = true;
      validation.errorMessages['required'] = `${label} is required`;
    }

    return {
      id,
      controlType: 'input',
      formControlName,
      config: {
        label,
        placeholder: `Enter ${label}`,
        type: 'tel',
        required,
        maxLength,
      },
      validation,
      gridPosition,
    };
  }

  /**
   * Creates an ABN (Australian Business Number) field
   *
   * Features:
   * - Validates ABN using ATO checksum algorithm
   * - Formats input as XX XXX XXX XXX
   * - Shows validation errors for invalid or incomplete ABNs
   */
  protected static createAbnField(
    options: {
      id?: string;
      formControlName?: string;
      label?: string;
      required?: boolean;
      gridPosition?: GridPosition;
    } = {}
  ): FormField {
    const {
      id = 'abn',
      formControlName = 'abnNumber',
      label = 'ABN',
      required = true,
      gridPosition = GRID_POSITIONS.HALF_WIDTH,
    } = options;

    const errorMessages: Record<string, string> = {
      abnInvalid: 'Please enter a valid ABN',
      abnIncomplete: 'ABN must be 11 digits',
    };

    if (required) {
      errorMessages['required'] = `${label} is required`;
    }

    return {
      id,
      controlType: 'input',
      formControlName,
      config: {
        label,
        placeholder: 'XX XXX XXX XXX',
        type: 'text',
        required,
        maxLength: 14, // 11 digits + 3 spaces for formatted input
        tooltip: 'Australian Business Number (11 digits)',
        hint: undefined,
        inputType: 'abn', // Custom input type for ABN formatting
      },
      validation: {
        required,
        customValidators: [abnCustomValidator],
        errorMessages,
      },
      gridPosition,
    };
  }

  /**
   * Creates an address textarea field
   */
  protected static createAddressField(
    options: {
      id?: string;
      formControlName?: string;
      label?: string;
      required?: boolean;
      minLength?: number;
      maxLength?: number;
      rows?: number;
      gridPosition?: GridPosition;
    } = {}
  ): FormField {
    const {
      id = 'address',
      formControlName = 'address',
      label = 'Address',
      required = true,
      minLength = FIELD_LENGTHS.ADDRESS.min,
      maxLength = FIELD_LENGTHS.ADDRESS.max,
      rows = 3,
      gridPosition = GRID_POSITIONS.FULL_WIDTH,
    } = options;

    return {
      id,
      controlType: 'textarea',
      formControlName,
      config: {
        label,
        placeholder: `Enter ${label}`,
        rows,
        required,
        maxLength,
        tooltip: minLength
          ? `Enter the complete address (minimum ${minLength} characters)`
          : undefined,
      },
      validation: {
        required,
        minLength,
        maxLength,
        errorMessages: {
          required: `${label} is required`,
          minLength: `${label} must be at least ${minLength} characters`,
          maxLength: `${label} cannot exceed ${maxLength} characters`,
        },
      },
      gridPosition,
    };
  }

  /**
   * Creates a website URL input field
   */
  protected static createUrlField(
    options: {
      id?: string;
      formControlName?: string;
      label?: string;
      required?: boolean;
      gridPosition?: GridPosition;
    } = {}
  ): FormField {
    const {
      id = 'website',
      formControlName = 'websiteUrl',
      label = 'Website',
      required = false,
      gridPosition = GRID_POSITIONS.FULL_WIDTH,
    } = options;

    const validation: any = {
      pattern: VALIDATION_PATTERNS.URL,
      errorMessages: {
        pattern: 'Please enter a valid website URL',
      },
    };

    if (required) {
      validation.required = true;
      validation.errorMessages['required'] = `${label} is required`;
    }

    return {
      id,
      controlType: 'input',
      formControlName,
      config: {
        label,
        placeholder: `Enter ${label} URL`,
        type: 'url',
        required,
      },
      validation: required
        ? validation
        : {
            pattern: VALIDATION_PATTERNS.URL,
            errorMessages: { pattern: 'Please enter a valid website URL' },
          },
      gridPosition,
    };
  }

  /**
   * Creates a select/dropdown field
   */
  protected static createSelectField(options: {
    id: string;
    formControlName: string;
    label: string;
    data?: any[];
    textField?: string;
    valueField?: string;
    valuePrimitive?: boolean;
    required?: boolean;
    placeholder?: string;
    gridPosition?: GridPosition;
  }): FormField {
    const {
      id,
      formControlName,
      label,
      data = [],
      textField = 'name',
      valueField = 'id',
      valuePrimitive = true,
      required = false,
      placeholder = `Select ${label}`,
      gridPosition = GRID_POSITIONS.HALF_WIDTH,
    } = options;

    return {
      id,
      controlType: 'select',
      formControlName,
      config: {
        label,
        placeholder,
        required,
        textField,
        valueField,
        valuePrimitive,
        data,
      },
      validation: required
        ? {
            required,
            errorMessages: {
              required: `${label} is required`,
            },
          }
        : undefined,
      gridPosition,
    };
  }

  /**
   * Creates a checkbox field
   */
  protected static createCheckboxField(options: {
    id: string;
    formControlName: string;
    label: string;
    defaultValue?: boolean;
    gridPosition?: GridPosition;
  }): FormField {
    const {
      id,
      formControlName,
      label,
      defaultValue = false,
      gridPosition = GRID_POSITIONS.FULL_WIDTH,
    } = options;

    return {
      id: id,
      controlType: 'checkbox',
      formControlName: formControlName,
      config: {
        options: [
          {
            // id: id,
            label: label,
            value: true,
            checked: false,
          },
        ],
        multiple: false,
        label: '',
      },
      value: defaultValue,
      gridPosition: { colSpan: 12 },
    };
  }

  /**
   * Creates a file upload field
   */
  protected static createFileField(options: {
    id: string;
    formControlName: string;
    label?: string;
    type?: 'image' | 'document' | 'any';
    maxFileSize?: number;
    acceptedFileTypes?: string[];
    showPreview?: boolean;
    gridPosition?: GridPosition;
    validateDimensions?: boolean;
    minWidth?: number;
    maxWidth?: number;
    minHeight?: number;
    maxHeight?: number;
    hint?: string;
    required?: boolean;
    enableCropper?: boolean;
    cropperAspectRatio?: number;
    cropperTargetWidth?: number;
    cropperTargetHeight?: number;
    cropperFormat?: 'png' | 'jpeg';
  }): FormField {
    const {
      id,
      formControlName,
      label = 'File',
      type = 'any',
      maxFileSize = 5,
      acceptedFileTypes,
      showPreview = type === 'image',
      gridPosition = GRID_POSITIONS.HALF_WIDTH,
      validateDimensions,
      minWidth,
      maxWidth,
      minHeight,
      maxHeight,
      hint,
      required = false,
      enableCropper,
      cropperAspectRatio,
      cropperTargetWidth,
      cropperTargetHeight,
      cropperFormat,
    } = options;

    let defaultTypes: string[] | undefined;

    if (type === 'image') {
      defaultTypes = ['image/png', 'image/jpg', 'image/jpeg'];
    } else if (type === 'document') {
      defaultTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];
    }

    return {
      id,
      controlType: 'file',
      formControlName,
      config: {
        type,
        showPreview,
        required,
        customConfig: {
          labelText: label,
          selectButtonText: `Upload ${label}`,
          placeholderText: `Select ${label.toLowerCase()}`,
          maxFileSize,
          showValidationError: true,
          acceptedFileTypes: acceptedFileTypes ?? defaultTypes,
          ...(validateDimensions !== undefined && { validateDimensions }),
          ...(minWidth !== undefined && { minWidth }),
          ...(maxWidth !== undefined && { maxWidth }),
          ...(minHeight !== undefined && { minHeight }),
          ...(maxHeight !== undefined && { maxHeight }),
          ...(hint !== undefined && { hint }),
          ...(enableCropper !== undefined && { enableCropper }),
          ...(cropperAspectRatio !== undefined && { cropperAspectRatio }),
          ...(cropperTargetWidth !== undefined && { cropperTargetWidth }),
          ...(cropperTargetHeight !== undefined && { cropperTargetHeight }),
          ...(cropperFormat !== undefined && { cropperFormat }),
        },
      },
      validation: required
        ? {
            required,
            errorMessages: {
              required: `${label} is required`,
            },
          }
        : undefined,
      gridPosition,
    };
  }

  /**
   * Creates a name field with standard validation
   */
  protected static createNameField(options: {
    id?: string;
    formControlName?: string;
    label: string;
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    gridPosition?: GridPosition;
  }): FormField {
    const {
      id = 'name',
      formControlName = 'name',
      label,
      required = true,
      minLength = FIELD_LENGTHS.NAME.min,
      maxLength = FIELD_LENGTHS.NAME.max,
      gridPosition = GRID_POSITIONS.HALF_WIDTH,
    } = options;

    return this.createTextField({
      id,
      formControlName,
      label,
      required,
      minLength,
      maxLength,
      gridPosition,
    });
  }

  /**
   * Applies view mode to all fields in a config (disables them)
   */
  protected static applyViewMode(config: DynamicFormConfig): DynamicFormConfig {
    const viewConfig = { ...config };
    viewConfig.sections = config.sections.map((section) => ({
      ...section,
      fields: section.fields.map((field) => ({
        ...field,
        config: field.config ? { ...field.config, disabled: true } : { disabled: true },
        fieldGroupConfig: field.fieldGroupConfig
          ? {
              ...field.fieldGroupConfig,
              readonly: true,
              fields: field.fieldGroupConfig.fields.map((groupField) => ({
                ...groupField,
                config: groupField.config
                  ? { ...groupField.config, disabled: true }
                  : { disabled: true },
              })),
            }
          : undefined,
      })),
    }));
    viewConfig.showActions = false;
    return viewConfig;
  }

  /**
   * Creates a standard form config wrapper
   */
  protected static createFormConfig(options: {
    sections: DynamicFormConfig['sections'];
    submitText?: string;
    cancelText?: string;
    showActions?: boolean;
    validateOnSubmit?: boolean;
    validateOnBlur?: boolean;
  }): DynamicFormConfig {
    return {
      sections: options.sections,
      submitText: options.submitText ?? 'Save',
      cancelText: options.cancelText ?? 'Cancel',
      showActions: options.showActions ?? true,
      validateOnSubmit: options.validateOnSubmit ?? true,
      validateOnBlur: options.validateOnBlur ?? true,
    };
  }
}
